"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useFormatter, useTranslations } from "next-intl";
import ModalComponent from "@/components/modals/ModalComponent";
import TableComponent from "@/components/ui/tables/TableComponent";
import type { SatelliteData, SatellitePosition } from "./earth-globe-r3f";

// Fila de tabla para la alternativa textual de satélites
type SatelliteTableRow = {
  id: string;
  satellite: string;
  altitudeValue: number;
  altitude: string;
  speedValue: number;
  speed: string;
  headingValue: number;
  heading: string;
  position: string;
};
interface GlobeTextualAlternativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  satellites: SatelliteData[];
  livePositions: SatellitePosition[];
}

/**
 * Modal de alternativa textual para la trayectoria de satélites del globo.
 * Muestra una descripción general, una tabla con los datos de cada satélite
 * y una sección de presentación.
 */
export default function GlobeTextualAlternativeModal(t0) {
  const $ = _c(100);
  const {
    isOpen,
    onClose,
    satellites,
    livePositions
  } = t0;
  const t = useTranslations("EarthGlobe");
  const ta = useTranslations("EarthGlobe.TextualAlternative");
  const formatter = useFormatter();
  let t1;
  if ($[0] !== formatter || $[1] !== livePositions || $[2] !== satellites) {
    let t2;
    if ($[4] !== formatter || $[5] !== livePositions) {
      t2 = (sat, index) => {
        const pos = livePositions[index];
        return {
          id: sat.id,
          satellite: sat.name,
          altitudeValue: sat.altitude,
          altitude: `${formatter.number(sat.altitude, {
            notation: "compact",
            compactDisplay: "short",
            maximumFractionDigits: 2
          })} km`,
          speedValue: sat.speed,
          speed: `${formatter.number(sat.speed, {
            maximumFractionDigits: 2
          })} km/h`,
          headingValue: sat.heading,
          heading: `${formatter.number(sat.heading, {
            maximumFractionDigits: 2
          })}°`,
          position: pos ? `${formatter.number(pos.latitude, {
            maximumFractionDigits: 2
          })}°, ${formatter.number(pos.longitude, {
            maximumFractionDigits: 2
          })}°` : `${formatter.number(sat.currentPosition.latitude, {
            maximumFractionDigits: 2
          })}°, ${formatter.number(sat.currentPosition.longitude, {
            maximumFractionDigits: 2
          })}°`
        };
      };
      $[4] = formatter;
      $[5] = livePositions;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = satellites.map(t2);
    $[0] = formatter;
    $[1] = livePositions;
    $[2] = satellites;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const rows = t1;
  let t2;
  if ($[7] !== ta) {
    t2 = ta("satellite");
    $[7] = ta;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = {
      accessorKey: "satellite",
      header: t2,
      enableSorting: true
    };
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t("altitude");
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = {
      accessorKey: "altitudeValue",
      header: t4,
      enableSorting: true,
      cell: _temp
    };
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("speed");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = {
      accessorKey: "speedValue",
      header: t6,
      enableSorting: true,
      cell: _temp2
    };
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("heading");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = {
      accessorKey: "headingValue",
      header: t8,
      enableSorting: true,
      cell: _temp3
    };
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("position");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = {
      accessorKey: "position",
      header: t10,
      enableSorting: true
    };
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t3 || $[29] !== t5 || $[30] !== t7 || $[31] !== t9) {
    t12 = [t3, t5, t7, t9, t11];
    $[27] = t11;
    $[28] = t3;
    $[29] = t5;
    $[30] = t7;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const columns = t12;
  let t13;
  if ($[33] !== t) {
    t13 = t("title");
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== ta) {
    t14 = ta("overview");
    $[35] = ta;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14) {
    t15 = <h3>{t14}</h3>;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== ta) {
    t16 = ta("overviewText");
    $[39] = ta;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16) {
    t17 = <p>{t16}</p>;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== ta) {
    t18 = ta("overviewText2");
    $[43] = ta;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t18) {
    t19 = <p>{t18}</p>;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t15 || $[48] !== t17 || $[49] !== t19) {
    t20 = <section className="modal__leftAlign__section">{t15}{t17}{t19}</section>;
    $[47] = t15;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== ta) {
    t21 = ta("values");
    $[51] = ta;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t21) {
    t22 = <h3>{t21}</h3>;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== ta) {
    t23 = ta("valuesText");
    $[55] = ta;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t23) {
    t24 = <p>{t23}</p>;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t22 || $[60] !== t24) {
    t25 = <section className="modal__leftAlign__section">{t22}{t24}</section>;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      marginBottom: "-16px",
      fontWeight: 700,
      fontSize: "16px",
      lineHeight: "18px",
      letterSpacing: "0.4%",
      color: "var(--primary)"
    };
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== ta) {
    t27 = ta("trajectoryMap");
    $[63] = ta;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t27) {
    t28 = <h3 style={t26}>{t27}</h3>;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== rows.length) {
    t29 = {
      total: rows.length,
      totalPages: 1
    };
    $[67] = rows.length;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== columns || $[70] !== rows || $[71] !== t29) {
    t30 = <TableComponent totalData={t29} data={rows} columns={columns} showCheckboxes={false} />;
    $[69] = columns;
    $[70] = rows;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== ta) {
    t31 = ta("presentation");
    $[73] = ta;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t31) {
    t32 = <h3>{t31}</h3>;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== ta) {
    t33 = ta("presentationText");
    $[77] = ta;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t33) {
    t34 = <p>{t33}</p>;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== ta) {
    t35 = ta("presentationText2");
    $[81] = ta;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== t35) {
    t36 = <p>{t35}</p>;
    $[83] = t35;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== t32 || $[86] !== t34 || $[87] !== t36) {
    t37 = <section className="modal__leftAlign__section">{t32}{t34}{t36}</section>;
    $[85] = t32;
    $[86] = t34;
    $[87] = t36;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t20 || $[90] !== t25 || $[91] !== t28 || $[92] !== t30 || $[93] !== t37) {
    t38 = <article className="modal__leftAlign">{t20}{t25}{t28}{t30}{t37}</article>;
    $[89] = t20;
    $[90] = t25;
    $[91] = t28;
    $[92] = t30;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== isOpen || $[96] !== onClose || $[97] !== t13 || $[98] !== t38) {
    t39 = <ModalComponent title={t13} onClose={onClose} isOpen={isOpen} titleAndAria="buttonCloseModal" buttonConfirmText="buttonAccept" onConfirm={onClose}>{t38}</ModalComponent>;
    $[95] = isOpen;
    $[96] = onClose;
    $[97] = t13;
    $[98] = t38;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  return t39;
}
function _temp3(t0) {
  const {
    row: row_1
  } = t0;
  return row_1.original.heading;
}
function _temp2(t0) {
  const {
    row: row_0
  } = t0;
  return row_0.original.speed;
}
function _temp(t0) {
  const {
    row
  } = t0;
  return row.original.altitude;
}